import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { authAPI, userAPI } from '../api';
import axios from '../lib/axios';
import { User } from '../types/models/user';
import { clearUnsaved } from '../lib/unsavedChanges';
import { acceptAuthToken, clearLocalStorageKeepingLastStores } from '../lib/authToken';
import { CAST_ATTENDANCE_UPDATED } from '../lib/castRefresh';
import {
  CompanyMembership,
  resolvePostLoginDestination,
  setCurrentCompanyId,
  setMemberships,
  shouldClearToken,
} from '../lib/companyContext';

/**
 * ページ遷移前にオーバーレイ/スクロールロック/フォーカスロックを強制解除する。
 * ログイン成功後の暗転フリーズを防ぐための応急処置。
 */
function cleanupOverlaysBeforeNavigation(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  try {
    // 1. Radix Portal を削除
    document.querySelectorAll('[data-radix-portal]').forEach(el => {
      try { el.remove(); } catch {}
    });

    // 2. Chakra Portal を削除
    document.querySelectorAll('.chakra-portal, #chakra-portal').forEach(el => {
      try { el.remove(); } catch {}
    });

    // 3. body/html の scroll-lock 解除
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    document.body.style.pointerEvents = '';

    // 4. aria-hidden / inert 解除
    document.body.removeAttribute('aria-hidden');
    document.body.removeAttribute('inert');

    // 5. aria-hidden が残っている要素から削除（body以外）
    document.querySelectorAll('[aria-hidden="true"]').forEach(el => {
      if (el !== document.body) {
        try { el.removeAttribute('aria-hidden'); } catch {}
      }
    });

    // 6. focus-lock 系の属性を解除
    document.querySelectorAll('[data-focus-lock], [data-focus-lock-disabled]').forEach(el => {
      try {
        el.removeAttribute('data-focus-lock');
        el.removeAttribute('data-focus-lock-disabled');
      } catch {}
    });

    // 7. react-remove-scroll 系のクラス/スタイルを解除（念のため）
    document.body.classList.remove('scroll-locked');
    document.documentElement.classList.remove('scroll-locked');

    console.log('[AuthContext] cleanupOverlaysBeforeNavigation completed');
  } catch (error) {
    // cleanup 自体でエラーが発生しても遷移は続行
    console.warn('[AuthContext] cleanupOverlaysBeforeNavigation error:', error);
  }
}

// 開発環境向け: 暗転時の緊急復旧コマンド
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  (window as any).__LOGME_FORCE_UNBLOCK_UI__ = cleanupOverlaysBeforeNavigation;
}



interface UserStatus {
  exists: boolean;
  email_verified: boolean;
  company_id: number | null;
  needs_setup: boolean;
}

interface RoleInfo {
  role_type: 'ADMIN' | 'STAFF' | 'CAST';
  stage_name?: string;
  display_name: string;
  is_admin: boolean;
  is_staff: boolean;
  is_cast: boolean;
}

interface Employee {
  id: number;
  employee_cd: string;
  role_type: 'ADMIN' | 'STAFF' | 'CAST';
  stage_name?: string;
  display_name: string;
  last_name: string;
  first_name: string;
  // マルチロール対応フラグ
  is_admin?: boolean;
  is_staff?: boolean;
  is_cast?: boolean;
  // 店舗情報
  store_id?: number;
  store?: {
    id: number;
    store_cd: string;
    store_name: string;
    description?: string;
  };
  // その他の基本プロパティ
  company_id?: number;
  user_id?: number | null;
  last_name_kana?: string;
  first_name_kana?: string;
  gender?: 'male' | 'female' | 'other';
  birth_date?: string;
  email?: string;
  phone?: string | null;
  emergency_contact?: string | null;
  hire_date?: string;
  contract_type?: 'full_time' | 'fixed_term' | 'part_time' | 'dispatch' | 'outsourced' | 'casual_daily' | 'intern_trainee';
  position?: string | null;
  department?: string | null;
  work_start_time?: string | null;
  work_end_time?: string | null;
  break_start_time?: string | null;
  break_end_time?: string | null;
  is_manager?: boolean;
  can_approve?: boolean;
  approval_level?: number;
  is_active?: boolean;
  status?: 'active' | 'inactive' | 'suspended' | 'retired';
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
}

interface AuthContextType {
  user: User | null;
  employee: Employee | null;
  roleInfo: RoleInfo | null;
  loading: boolean;
  loggingOut: boolean;
  authenticated: boolean;
  login: (email: string, password: string) => Promise<{ error?: any } | void>;
  logout: () => Promise<void>;
  register: (userData: any) => Promise<void>;
  refreshUser: () => Promise<void>;
  /** 所属の一覧だけを取り直す（user / employee は差し替えない＝画面の中身を外さない） */
  refreshMemberships: () => Promise<void>;
  /** ログイン状態を確かめる通信に失敗した（電波・タイムアウト・サーバ側の失敗）。ログイン情報は残している */
  connectionError: boolean;
  /** connectionError のとき、もう一度ログイン状態を確かめる */
  retryAuth: () => Promise<void>;
  checkUserStatus: (email: string) => Promise<UserStatus>;
  setupComplete: (token: string, userData: User) => Promise<void>;
  /** 今の会社（トークンの会社。2026-10-17・複数の会社に所属するアカウント） */
  currentCompanyId: number | null;
  /** 所属しているお店の一覧（会社ごと）。古い API の応答では空 */
  companies: CompanyMembership[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [roleInfo, setRoleInfo] = useState<RoleInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const [connectionError, setConnectionError] = useState(false);
  const [currentCompanyId, setCurrentCompanyIdState] = useState<number | null>(null);
  const [companies, setCompanies] = useState<CompanyMembership[]>([]);
  // 直前の fetchUser が失敗した理由。'auth' = ログインが無効（401/403 など）、'network' = 通信・サーバ側の失敗
  const lastFetchFailureRef = useRef<'auth' | 'network' | null>(null);
  const isMountedRef = useRef(true);

  // コンポーネントのアンマウントを追跡
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ユーザー情報を取得する関数
  const fetchUser = useCallback(async (token: string): Promise<User | null> => {
    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await userAPI.getCurrentUser();
      console.log('AuthContext::fetchUser - Response:', response);

      // レスポンスの成功フラグをチェック
      if (response && response.success && response.data) {
        const userData = response.data;

        // employeeとroleInfoを設定
        const employee = userData?.employee as unknown as Employee | undefined;
        const isAdmin = employee.is_admin || employee.role_type === 'ADMIN';
        const roleInfo = employee ? {
          role_type: employee.role_type,
          stage_name: employee.stage_name,
          display_name: employee.stage_name || `${employee.last_name} ${employee.first_name}`,
          is_admin: isAdmin,
          is_staff: employee.is_staff || employee.role_type === 'STAFF' || isAdmin,
          is_cast: employee.is_cast || employee.role_type === 'CAST',
        } : null;

        setEmployee(employee);
        setRoleInfo(roleInfo);
        lastFetchFailureRef.current = null;
        setConnectionError(false);

        // 今の会社と所属の一覧（タブのメモリ）。応答が companies を持たない（古い API）ときも 1 社として動く
        const data = userData as any;
        const companyId = data?.current_company_id ?? data?.company_id ?? null;
        setCurrentCompanyId(companyId);
        setMemberships(data?.companies);
        setCurrentCompanyIdState(typeof companyId === 'number' ? companyId : null);
        setCompanies(Array.isArray(data?.companies) ? data.companies : []);

        return userData;
      } else {
        throw new Error('認証に失敗しました');
      }
    } catch (error: any) {
      console.warn('AuthContext::fetchUser - Error:', error);
      // 🔴 ログイン情報を消すのは「ログインが無効」とサーバが答えたときだけ（2026-10-01・UX 監査 K-2）。
      //    以前は通信できない・タイムアウト・サーバの一時的な失敗でも消していたので、地下の店で電波が弱いときに
      //    アプリを開くとログアウトされ、出勤を押す前に LINE ログインからやり直しになっていた
      const status = error?.response?.status;
      // 通信の失敗 = 応答が届かなかった（電波・タイムアウト）か、サーバ側の一時的な失敗（5xx）だけ。
      // それ以外（401/403、success でない応答、画面側の例外）は従来どおりログイン情報を消してログイン画面へ。
      // ⚠️ 画面側の例外まで通信の失敗にしない。例えば employee が無い応答（会社の無いユーザー）は上で必ず例外になり、
      //    「通信できませんでした」から抜け出せなくなる（ログアウトの導線も無い）
      const isNetworkFailure = (!!error?.isAxiosError && !error?.response) || (typeof status === 'number' && status >= 500);
      const isAuthFailure = !isNetworkFailure;
      lastFetchFailureRef.current = isAuthFailure ? 'auth' : 'network';
      if (isAuthFailure) {
        // 直前が通信の失敗でも、ログインが無効と分かったら「ログインはそのまま」の画面は出さない（ログイン画面へ）
        setConnectionError(false);
        // 🔴 消すのは、この要求が送ったトークン＝今のトークンのときだけ（2026-10-17・通信の共通処理と同じ判定）。
        //    切り替え・ログインで新しいトークンに替わった後の古い要求の失敗で、新しいトークンを消さない
        const currentToken = (() => { try { return localStorage.getItem('auth_token'); } catch { return null; } })();
        if (shouldClearToken(token, currentToken)) {
          try { localStorage.removeItem('auth_token'); } catch { }
          try { document.cookie = 'auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'; } catch { }
          delete axios.defaults.headers.common['Authorization'];
        }
      } else {
        setConnectionError(true);
      }
      return null;
    }
  }, []);

  // ユーザー情報を更新する関数
  const refreshUser = useCallback(async () => {
    if (!isMountedRef.current) return;

    const token = localStorage.getItem('auth_token');
    if (token) {
      const userData = await fetchUser(token);
      if (isMountedRef.current) {
        setUser(userData);
      }
    }
  }, [fetchUser]);

  /**
   * 所属の一覧（companies）だけを取り直す（2026-10-18）。user / employee は差し替えない。
   * 🔴 refreshUser は user / employee に新しいオブジェクトを入れるので、StoreProvider が店を読み直し、
   *    MainLayout が店の要る画面を骨組み表示に差し替える＝**書きかけの設定カードが外れて未保存の入力が消える**。
   *    別の会社への切り替えの確認の前・出勤・退勤の後は、こちらを使う
   */
  const refreshMemberships = useCallback(async () => {
    try {
      const response = await userAPI.getCurrentUser();
      const data = (response as any)?.data;
      if (!(response as any)?.success || !data || !isMountedRef.current) return;
      setMemberships(data.companies);
      setCompanies(Array.isArray(data.companies) ? data.companies : []);
    } catch {
      // 取り直せなくても今の表示のまま（ログイン状態の判断は fetchUser の役目）
    }
  }, []);

  // 初回ロード時の認証チェック
  useEffect(() => {
    const initAuth = async () => {
      try {
        // localStorageとクッキーの両方からトークンを取得
        let token = localStorage.getItem('auth_token');

        if (!token) {
          // localStorageにない場合はクッキーから取得
          const cookies = document.cookie.split(';');
          const authCookie = cookies.find(cookie => cookie.trim().startsWith('auth_token='));
          if (authCookie) {
            token = authCookie.split('=')[1];
            // localStorageにも保存しておく
            localStorage.setItem('auth_token', token);
          }
        }

        if (token) {
          const userData = await fetchUser(token);
          if (isMountedRef.current) {
            setUser(userData);
            // ユーザーデータが取得できない場合はトークンを削除。ただし通信の失敗ではログイン情報を残す
            if (!userData && lastFetchFailureRef.current !== 'network') {
              localStorage.removeItem('auth_token');
              document.cookie = 'auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
            }
          }
        }

        if (isMountedRef.current) {
          setLoading(false);
        }
      } catch (error) {
        console.warn('AuthContext::initAuth - Error:', error);
        // エラーが発生した場合はトークンを削除
        try { localStorage.removeItem('auth_token'); } catch { }
        try { document.cookie = 'auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'; } catch { }

        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    initAuth();
  }, [fetchUser]);

  // 通信の失敗でログイン状態を確かめられなかったとき、もう一度確かめる（「通信できません」画面の「もう一度読み込む」）
  const retryAuth = useCallback(async () => {
    const token = (() => { try { return localStorage.getItem('auth_token'); } catch { return null; } })();
    if (!token) {
      setConnectionError(false);
      return;
    }
    setLoading(true);
    const userData = await fetchUser(token);
    if (isMountedRef.current) {
      setUser(userData);
      setLoading(false);
    }
  }, [fetchUser]);

  // 認証状態の同期チェック（初回のみ。タブ切替でのリロード連鎖を防ぐ）
  const checkAuthConsistency = useCallback(() => {
    if (typeof window === 'undefined') return;
    const cookies = document.cookie.split(';');
    const authCookie = cookies.find(cookie => cookie.trim().startsWith('auth_token='));
    const hasLocalStorageToken = !!localStorage.getItem('auth_token');

    // クッキーが消えているがlocalStorageにトークンがある場合、
    // Safari ITP や外部アプリ（PassPRNT等）からの復帰でクッキーが消えることがあるため
    // まずクッキーを復元して認証を維持する
    if (!authCookie && hasLocalStorageToken) {
      const token = localStorage.getItem('auth_token');
      if (token) {
        console.log('AuthContext: Cookie missing but localStorage token exists, restoring cookie');
        document.cookie = `auth_token=${token}; path=/; max-age=86400; SameSite=Lax`;
        return;
      }
    }

    // ローカルストレージにトークンがあるが、ユーザーがnullの場合
    if (hasLocalStorageToken && !user) {
      console.log('AuthContext: localStorage token exists but user is null, attempting to fetch user');
      const token = localStorage.getItem('auth_token');
      if (token) {
        fetchUser(token).then(userData => {
          if (isMountedRef.current) {
            setUser(userData);
          }
        }).catch(() => {
          localStorage.removeItem('auth_token');
          delete axios.defaults.headers.common['Authorization'];
          window.location.href = '/login';
        });
      }
    }
  }, [user, fetchUser]);

  useEffect(() => {
    if (!loading) {
      checkAuthConsistency();
    }
  }, [loading, checkAuthConsistency]);

  // 所属が 2 社以上の人は、出勤・退勤の後に所属の一覧を取り直す（店舗セレクタの「出勤中」が古くならないように。設計書 §4）。
  // user / employee は差し替えない（差し替えると画面の中身が一度外れる・refreshMemberships の注記）
  const multiCompany = companies.length >= 2;
  useEffect(() => {
    if (!multiCompany || typeof window === 'undefined') return;
    const onAttendanceUpdated = () => { void refreshMemberships(); };
    window.addEventListener(CAST_ATTENDANCE_UPDATED, onAttendanceUpdated);
    return () => window.removeEventListener(CAST_ATTENDANCE_UPDATED, onAttendanceUpdated);
  }, [multiCompany, refreshMemberships]);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const response = await authAPI.login({ email, password });
      // Backend returns role_info (snake_case); normalize to roleInfo (camelCase)
      const normalized = {
        ...response,
        roleInfo: (response as any).roleInfo ?? (response as any).role_info,
      } as typeof response & { roleInfo?: any };
      const { token, user: userData, employee: employeeData, roleInfo: roleInfoData, dashboard_path } = normalized;
      const loginData = response as any;

      // 既存ユーザーと異なる場合はローカルストレージをクリア（ユーザー切替時の残存データ防止）。
      // その端末で前回選んだ店（ユーザーごと）は残す（共用タブレットの店選びの並びに使う）
      try {
        const prevEmail = localStorage.getItem('current_user_email');
        if (prevEmail && prevEmail !== email) {
          clearLocalStorageKeepingLastStores();
        }
      } catch {}

      // トークン・今の会社・選んでいる店を同じ場所で書く（2026-10-17。今の会社の店に無い保存値は消す）
      acceptAuthToken({
        token,
        currentCompanyId: loginData?.current_company_id ?? userData?.company_id ?? null,
        companies: loginData?.companies ?? null,
        userId: userData?.id ?? null,
      });

      // 🆕 所属が 2 社以上なら、ユーザーの状態を入れる**前に**「どのお店で使いますか」へ（2026-10-17）。
      //    🔴 setUser の後にすると、ログイン画面の自動の転送（RequireGuest・checkLoginAccess）が、仮の会社の役割のホームへ並走して移り、
      //    店を選ぶ前に仮の会社の画面（定期取得・打刻）が立ち上がる。redirect は選んだ会社での役割で判定し直すので持ち越す
      if (loginData?.requires_store_selection) {
        try {
          if (userData?.id) localStorage.setItem('current_user_id', String(userData.id));
          localStorage.setItem('current_user_email', userData?.email || email);
        } catch {}
        const pending = new URLSearchParams(window.location.search).get('redirect');
        cleanupOverlaysBeforeNavigation();
        window.location.href = pending ? `/select-store?redirect=${encodeURIComponent(pending)}` : '/select-store';
        return;
      }

      // ユーザー情報はフルモデルを取得して整合性を保つ
      console.log('Login response data:', { userData, employeeData, roleInfoData });
      const fetchedUser = await fetchUser(token);
      setUser(fetchedUser);

      // employee / roleInfo はフェッチ済みユーザーから再構築（フォールバックでレスポンスも参照）
      const employee = (fetchedUser?.employee as unknown as Employee) || (employeeData as unknown as Employee | undefined);
      const isAdminFallback = employee ? (employee.is_admin || employee.role_type === 'ADMIN') : false;
      const roleInfo = roleInfoData || (employee ? {
        role_type: employee.role_type,
        stage_name: employee.stage_name,
        display_name: employee.stage_name || `${employee.last_name} ${employee.first_name}`,
        is_admin: isAdminFallback,
        is_staff: employee.is_staff || employee.role_type === 'STAFF' || isAdminFallback,
        is_cast: employee.is_cast || employee.role_type === 'CAST' || isAdminFallback,
      } : null);

      setEmployee(employee || null);
      setRoleInfo(roleInfo || null);

      // 現在のユーザー識別情報を保存（次回ログイン時の切替判定に使用）
      try {
        if (fetchedUser?.id) localStorage.setItem('current_user_id', String(fetchedUser.id));
        localStorage.setItem('current_user_email', fetchedUser?.email || email);
      } catch {}

      // 認証後のユーザーステータスを確認（メール未認証/セットアップ未完了をガード）
      // LINE-only ユーザー（email なし）はこのチェックをスキップ
      if (email) {
        try {
          const status = await authAPI.checkStatus({ email });
          if (!status.email_verified) {
            cleanupOverlaysBeforeNavigation();
            window.location.href = `/email-verification?email=${encodeURIComponent(email)}`;
            return;
          }
          if (status.needs_setup) {
            cleanupOverlaysBeforeNavigation();
            window.location.href = `/setup?email=${encodeURIComponent(email)}`;
            return;
          }
        } catch (_) {
          // 失敗時は従来フローにフォールバック
        }
      }

      // リダイレクト先を確認（ロール別ダッシュボードを優先）
      const searchParams = new URLSearchParams(window.location.search);
      const redirect = searchParams.get('redirect');

      // redirect がある場合でも、ユーザーのロールに合わないセクション（例: CASTで/admin/*）は無視する（店選びの画面と同じ判定）
      const destination = resolvePostLoginDestination(employee?.role_type ?? null, dashboard_path, redirect);

      // ロール別ダッシュボードにリダイレクト（遷移前にオーバーレイをクリーンアップ）
      cleanupOverlaysBeforeNavigation();
      window.location.href = destination;

      // 成功時は何も返さない（void）
      return;
    } catch (error: any) {
      // エラーを適切にキャッチして、Runtime Errorを表示させない
      console.warn('Login error in AuthContext:', error);

      // エラーオブジェクトを返す（Promiseを適切に解決）
      return { error };
    }
  }, []);

  const logout = useCallback(async () => {
    setLoggingOut(true);
    try {
      // サーバーにログアウト通知（オプション）
      await authAPI.logout();
    } catch (error) {
      // エラーが発生してもログアウト処理を続行
    }

    // ローカルストレージをクリア（オンボーディング状態なども含めて全消去）。
    // その端末で前回選んだ店（ユーザーごと）だけは残す（共用タブレットで次に入る人の店選びの並びに使う）
    clearLocalStorageKeepingLastStores();
    setCurrentCompanyId(null);
    setMemberships([]);
    // 表示ロールの引き継ぎ記録は sessionStorage なので localStorage.clear() では消えない。
    // 残すと、共通ページ（/notifications 等）へ直接戻ったときに前ユーザーのロールを引き継ぐ。
    try { sessionStorage.clear(); } catch {}
    // クッキーも削除
    document.cookie = 'auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setEmployee(null);
    setRoleInfo(null);

    // 画面でログアウトを確認済みなので、未保存の警告（ブラウザ標準の確認）はここでは出さない。
    // 出すと、ログアウト済みの状態のまま「キャンセル」で設定画面に取り残される（UX 監査 A-4 のレビュー）
    clearUnsaved();

    // ログインページにリダイレクト
    window.location.href = '/login';
  }, []);

  const register = useCallback(async (userData: any) => {
    try {
      await authAPI.register(userData);
      window.location.href = '/login?registered=true';
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '登録に失敗しました');
    }
  }, []);

  const checkUserStatus = useCallback(async (email: string): Promise<UserStatus> => {
    try {
      const response = await authAPI.checkStatus({
        email
      });
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'ユーザーステータスの確認に失敗しました');
    }
  }, []);

  const setupComplete = useCallback(async (token: string, userData: User) => {
    try {
      // トークンと今の会社を書く（作った会社。2026-10-17）
      acceptAuthToken({ token, currentCompanyId: (userData as any)?.company_id ?? null, userId: (userData as any)?.id ?? null });

      // ユーザー情報を設定
      setUser(userData);

      // セットアップ完了後は管理者ダッシュボードにリダイレクト（ロール別はクライアントで再選択）
      cleanupOverlaysBeforeNavigation();
      window.location.href = '/admin/dashboard';
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'セットアップ完了処理に失敗しました');
    }
  }, []);

  const value: AuthContextType = {
    user,
    employee,
    roleInfo,
    loading,
    loggingOut,
    authenticated: !!user,
    login,
    logout,
    register,
    refreshUser,
    refreshMemberships,
    connectionError,
    retryAuth,
    checkUserStatus,
    setupComplete,
    currentCompanyId,
    companies,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 
